
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  Button,
  Grid,
  TextField,
  Typography,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { DataGrid } from "@mui/x-data-grid";
import { Delete, Edit } from "@mui/icons-material";
import { Add as AddIcon } from "@mui/icons-material";
import { FileDownload, PictureAsPdf, Print } from "@mui/icons-material";
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { getVendors } from "../../State/Vendor/Action";
import { getProducts } from "../../State/Product/Action";
import { createCoupon, deleteCoupon, getAllCoupon, updateCoupon } from "../../State/Coupon/Action";
import { PermissionService } from "../../../config/permissionService";
import { getSelectedExportRows } from "../../../config/exportSelection";

const Coupons = () => {
  const { adminProduct, adminVendor, coupons } = useSelector((store) => store);
  const dispatch = useDispatch();

  const todayDateTime = dayjs().format("YYYY-MM-DDTHH:mm");

  const [formData, setFormData] = useState({
    vendorId: "",
    productId: "",
    productCategoryId: "",
    title: "",
    couponType: "percentage",
    couponPriceOrPercentage: "",
    startDate: "",
    endDate: "",
  });

  const [editingId, setEditingId] = useState(null);
  const [openAddCouponModal, setOpenAddCouponModal] = useState(false);
  const [couponToDelete, setCouponToDelete] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [selectedRowIds, setSelectedRowIds] = useState([]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    dispatch(getProducts());
    dispatch(getVendors());
    dispatch(getAllCoupon());
  }, [dispatch]);

  useEffect(() => {
    const selectedProduct = adminProduct.products?.response?.find(
      (p) => p.id === formData.productId
    );

    const productCategoryId = selectedProduct?.productCategory?.id || 0;

    if (formData.productCategoryId !== productCategoryId) {
      setFormData((prev) => ({
        ...prev,
        productCategoryId: productCategoryId,
      }));
    }
  }, [formData.productId, adminProduct.products]);


  const uniqueCategories = [
    ...new Map(
      adminProduct?.products?.response
        ?.filter(p =>
          (!formData?.vendorId || formData.vendorId === 0 || p.vendor.id === formData.vendorId) &&
          (!formData?.productId || formData.productId === 0 || p.id === formData.productId) &&
          p.productStatus === "APPROVED" &&
          p.vendor.approved &&
          !p.vendor.isBlocked
        )
        .map(p => p?.productCategory)
        .filter(cat => cat && cat.id)
        .map(cat => [cat.id, cat])
    ).values()
  ];

  const handleSubmit = (e) => {
    e.preventDefault();

    if (
      !formData.title.trim() ||
      // !formData.couponType ||
      !formData.couponPriceOrPercentage ||
      !formData.startDate ||
      !formData.endDate
    ) {
      toast.error("All fields are required");
      return;
    }

    if (formData.couponPriceOrPercentage <= 0 || formData.couponPriceOrPercentage >= 100) {
      toast.error("Discount percentage must be greater than 0 ot less than 100");
      return;
    }

    if (formData.vendorId === 0) {
      toast.error("Please select a vendor !!");
      return;
    }

    const formattedCouponData = {
      ...formData,
      couponType: "percentage",
      startDate: dayjs(formData.startDate).format("YYYY-MM-DDTHH:mm:ss"),
      endDate: dayjs(formData.endDate).format("YYYY-MM-DDTHH:mm:ss"),
    };

    if (editingId) {
      dispatch(updateCoupon(editingId, formattedCouponData));
    } else {
      if (formData.startDate <= todayDateTime) {
        toast.error("Start date must be in the future minutes");
        return;
      }
      if (formData.endDate <= formData.startDate) {
        toast.error("End date must be after the start date");
        return;
      }
      dispatch(createCoupon(formattedCouponData));
    }

    handleCloseAddCouponModal();

  };

  const handleCouponDelete = (couponId) => {
    setCouponToDelete(couponId);
    setOpenDialog(true);
  };

  const confirmDelete = () => {
    dispatch(deleteCoupon(couponToDelete));
    setOpenDialog(false);
    setCouponToDelete(null);
  };

  const cancelDelete = () => {
    setOpenDialog(false);
    setCouponToDelete(null);
  };

  const handleCouponUpdate = (item) => {
    setFormData({
      vendorId: item?.vendorId,
      productId: item?.productId,
      productCategoryId: item?.productCategoryId,
      title: item.title,
      couponType: item.couponPrice ? "price" : "percentage",
      couponPriceOrPercentage: item.couponPrice || item.couponPercentage,
      startDate: dayjs(item.startDate).format("YYYY-MM-DDTHH:mm"),
      endDate: dayjs(item.endDate).format("YYYY-MM-DDTHH:mm"),
    });
    setEditingId(item.id);
    setOpenAddCouponModal(true);
  };

  const handleCloseAddCouponModal = () => {
    setOpenAddCouponModal(false);
    setFormData({
      vendorId: "",
      productId: "",
      productCategoryId: "",
      title: "",
      couponType: "",
      couponPriceOrPercentage: "",
      startDate: "",
      endDate: "",
    });
    setEditingId(null);
  };

  const rows =
    coupons.coupons?.response?.map((coupon, index) => ({
      id: coupon.id,
      title: coupon?.title,
      couponCode: coupon?.couponCode,
      couponPercentage: coupon?.couponPercentage,
      couponPrice: coupon?.couponPrice,
      vendorId: coupon?.vendor?.id,
      vendorName: coupon?.vendor?.name,
      productId: coupon?.product?.id,
      productTitle: coupon?.product?.title,
      productCategoryId: coupon?.productCategory?.id,
      productCategoryName: coupon?.productCategory?.name,
      startDate: coupon.startDate,
      endDate: coupon.endDate,
    })) || [];

  const filteredRows = rows.filter((row) => {
    const query = searchQuery.toLowerCase();
    return (
      row.title?.toLowerCase()?.includes(query) ||
      row.couponCode?.toLowerCase()?.includes(query) ||
      String(row.couponPercentage)?.toLowerCase()?.includes(query) ||
      String(row.couponPrice)?.toLowerCase()?.includes(query) ||
      row.vendorName?.toLowerCase()?.includes(query) ||
      row.productTitle?.toLowerCase()?.includes(query) ||
      row.productCategoryName?.toLowerCase()?.includes(query) ||
      row.startDate?.toLowerCase()?.includes(query) ||
      row.endDate?.toLowerCase()?.includes(query)
    );
  });

  // Export, PDF, and Print Handlers
    const getExportData = () => getSelectedExportRows(filteredRows, selectedRowIds);

  const handleExportCSV = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const csvContent = exportRows.map((row, index) => {
      return [
        index + 1,
        row.title,
        row.couponCode,
        row.vendorName || "----",
        row.productTitle || "----",
        row.productCategoryName || "----",
        row.couponPercentage,
        row.startDate ? dayjs(row.startDate).format("DD MMM YYYY HH:mm") : "----",
        row.endDate ? dayjs(row.endDate).format("DD MMM YYYY HH:mm") : "----",
      ].join(',');
    }).join('\n');

    const headers = [
      'S.No.',
      'Title',
      'Coupon Code',
      'Vendor Name',
      'Product Title',
      'Product Category',
      'Discount(%)',
      'From Date',
      'To Date',
    ].join(',');

    const csv = `${headers}\n${csvContent}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'coupons.csv';
    link.click();
  };

  const handleExportPDF = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const doc = new jsPDF();
    doc.setFontSize(12);
    doc.text("Coupon List", 14, 15);

    const tableColumn = [
      'S.No.',
      'Title',
      'Coupon Code',
      'Vendor Name',
      'Product Title',
      'Product Category',
      'Discount(%)',
      'From Date',
      'To Date',
    ];
    const tableRows = [];

    exportRows.forEach((row, index) => {
      const formData = [
        index + 1,
        row.title,
        row.couponCode,
        row.vendorName || "----",
        row.productTitle || "----",
        row.productCategoryName || "----",
        row.couponPercentage,
        row.startDate ? dayjs(row.startDate).format("DD MMM YYYY HH:mm") : "----",
        row.endDate ? dayjs(row.endDate).format("DD MMM YYYY HH:mm") : "----",
      ];
      tableRows.push(formData);
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      theme: 'striped',
      headStyles: {
        fillColor: [37, 99, 235], // Tailwind blue-600 (RGB: 37,99,235)
        textColor: [255, 255, 255], // white text
        halign: 'left',
      },
      styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
      columnStyles: {
        0: { cellWidth: 10 }, // Coupon ID
        1: { cellWidth: 15 }, // Coupon ID
        2: { cellWidth: 20 }, // Product ID
        3: { cellWidth: 20 }, // Product Title
        4: { cellWidth: 25 }, // Discount
        5: { cellWidth: 20 }, // Start Date
        6: { cellWidth: 15 }, // End Date
        7: { cellWidth: 30 }, // End Date
        8: { cellWidth: 30 }, // End Date
      },
    });

    doc.save('coupons.pdf');
  };

  const handlePrint = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const printWindow = window.open('', '_blank');
    const headers = [
      'S.No.',
      'Title',
      'Coupon Code',
      'Vendor Name',
      'Product Title',
      'Product Category',
      'Discount(%)',
      'From Date',
      'To Date',
    ];

    const tableContent = exportRows.map((row, index) => [index + 1,
    row.title,
    row.couponCode,
    row.vendorName || "----",
    row.productTitle || "----",
    row.productCategoryName || "----",
    row.couponPercentage,
    row.startDate ? dayjs(row.startDate).format("DD MMM YYYY HH:mm") : "----",
    row.endDate ? dayjs(row.endDate).format("DD MMM YYYY HH:mm") : "----",
    ]);

    const html = `
      <html>
        <head>
          <title>Coupon List</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th {background: #449AEF;
                color: white;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact; }
            tr:nth-child(even) { background-color: #f5f5f5; }
          </style>
        </head>
        <body>
          <h2>Coupon List</h2>
          <table>
            <thead>
              <tr>
                ${headers.map(header => `<th>${header}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${tableContent.map(row => `
                <tr>
                  ${row.map(cell => `<td>${cell}</td>`).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  const writeAction = PermissionService.has({ category: "Admin", page: "Coupons", type: "WRITE" });

  const columns = [
    {
      field: "serialNo",
      headerName: "S.No.",
      width: 70,
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1
    },
    { field: "title", headerName: " Title", width: 100 },
    { field: "couponCode", headerName: " Code", flex: 1.4 },
    { field: "couponPercentage", headerName: "Value", width: 60, renderCell: (params) => params?.value ? `${params?.value}%` : params?.row?.couponPrice || 0 },
    { field: "vendorName", headerName: "Vendor Name", flex: 1, renderCell: (params) => params?.value || "----" },
    { field: "productTitle", headerName: "Product Title", flex: 1, renderCell: (params) => params?.value || "----" },
    { field: "productCategoryName", headerName: "Product Category Name", flex: 1, renderCell: (params) => params?.value || "----" },
    { field: "startDate", headerName: "From Date & Time", flex: 1, renderCell: (params) => params.value ? dayjs(params?.value).format("DD MMM YYYY HH:mm") : "----", },
    { field: "endDate", headerName: "To Date & Time", flex: 1, renderCell: (params) => params.value ? dayjs(params?.value).format("DD MMM YYYY HH:mm") : "----", },
    ...(writeAction ? [
      {
        field: "actions",
        headerName: "Action",
        width: 70,
        renderCell: (params) => (
          <Box display="flex" gap={1}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                sx={{ p: 0, m: 0, minWidth: 24, minHeight: 24, cursor: "pointer" }}
                onClick={() => handleCouponUpdate(params.row)}
              >
                <Edit fontSize="small" sx={{ fontSize: 15, cursor: "pointer" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                color="error"
                size="small"
                sx={{ p: 0, m: 0, minWidth: 24, minHeight: 24, cursor: "pointer" }}
                onClick={() => handleCouponDelete(params.row.id)}
              >
                <Delete fontSize="small" sx={{ fontSize: 15, cursor: "pointer" }} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ] : []),
  ];

  return (
    <div>

      <Dialog open={openAddCouponModal} onClose={handleCloseAddCouponModal} fullWidth maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", fontSize: "1.25rem", fontWeight: "bold", mt: 1, mb: 0.5, pb: 1, borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
          {editingId ? "UPDATE COUPON" : "ADD NEW COUPON"}
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} mt={1} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Vendor</InputLabel>
                <Select
                  name="vendorId"
                  size="small"
                  label="Vendor"
                  value={formData.vendorId}
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e5e7eb'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#ef4444'
                    }
                  }}
                >
                  <MenuItem value={0} >Select Vendor...</MenuItem>
                  {adminVendor?.vendors?.response
                    ?.filter((v) => v?.approved && !v?.isBlocked)
                    ?.map((vendor) => (
                      <MenuItem key={vendor.id} value={vendor.id}>
                        {vendor.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} mt={1} md={4}>
              <FormControl fullWidth size="small" variant="outlined"
              >
                <InputLabel>Product</InputLabel>
                <Select
                  id="productId"
                  name="productId"
                  label="Product"
                  value={formData.productId}
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e5e7eb'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#ef4444'
                    }
                  }}
                >
                  <MenuItem value={0}>
                    Select Product...
                  </MenuItem>
                  {adminProduct.products?.response
                    ?.filter(
                      (p) =>
                        (!formData?.vendorId || formData.vendorId === 0 || p.vendor.id === formData.vendorId) &&
                        p.productStatus === "APPROVED" && p.vendor.approved && !p.vendor.isBlocked
                    )
                    ?.map((product) => (
                      <MenuItem key={product.id} value={product.id}>
                        {product.title}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} mt={1} md={4}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel id="product-label">Product Category</InputLabel>
                <Select
                  labelId="product-label"
                  label="Product Category"
                  id="productCategoryId"
                  name="productCategoryId"
                  value={formData.productCategoryId}
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e5e7eb'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#ef4444'
                    }
                  }}
                >
                  <MenuItem value={0}>
                    Select Product Category...
                  </MenuItem>

                  {uniqueCategories?.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Title"
                name="title"
                size="small"
                value={formData.title}
                onChange={handleChange}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e5e7eb'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#ef4444'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={formData.couponType === "price" ? "Coupon price" : "Coupon Percentage"}
                name="couponPriceOrPercentage"
                size="small"
                value={formData.couponPriceOrPercentage}
                onChange={handleChange}
                inputProps={{ maxLength: 2 }}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e5e7eb'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#ef4444'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="From Date & Time"
                name="startDate"
                type="datetime-local"
                size="small"
                value={formData.startDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: todayDateTime }}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e5e7eb'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#ef4444'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="To Date & Time"
                name="endDate"
                type="datetime-local"
                size="small"
                value={formData.endDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: formData.startDate }}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e5e7eb'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#ef4444'
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ display: "flex", justifyContent: "center", gap: 2, p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
          <Button
            variant="outlined"
            onClick={handleCloseAddCouponModal}
            sx={{
              color: "#449AEF",
              borderColor: "#449AEF",
              fontWeight: 600,
              fontSize: "0.85rem",
              borderRadius: "8px",
              px: 3,
              py: 1.2,
              transition: "all 0.2s",
              "&:hover": {
                backgroundColor: "#449AEF",
                color: "#fff",
                borderColor: "#449AEF",
                boxShadow: "0 2px 8px rgba(239,68,68,0.1)",
              },
              width: { xs: "100%", sm: "auto" },
              textAlign: "center"
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            onClick={handleSubmit}
            sx={{
              background: "linear-gradient(90deg, #449AEF 0%, #f87171 100%)",
              color: "#fff",
              fontWeight: 600,
              fontSize: "0.85rem",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(239,68,68,0.15)",
              px: 3,
              py: 1.2,
              transition: "all 0.2s",
              "&:hover": {
                background: "linear-gradient(90deg, #f87171 0%, #449AEF 100%)",
                boxShadow: "0 4px 16px rgba(239,68,68,0.25)",
                transform: "scale(1.02)",
              },
              width: { xs: "100%", sm: "auto" },
              textAlign: "center"
            }}
          >
            {editingId ? "Update" : "Add"} Coupon
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialog} onClose={cancelDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this Coupon?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete}>Cancel</Button>
          <Button onClick={confirmDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
        <CardHeader
          title={
            <Typography variant="h6" className="text-gray-700">
              COUPONS
            </Typography>
          }
          action={writeAction && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                sx={{
                  background: "linear-gradient(90deg, #449AEF 0%, #f87171 100%)",
                  color: "#fff",
                  boxShadow: "0 2px 8px rgba(239,68,68,0.15)",
                  transition: "all 0.2s",
                  "&:hover": {
                    background: "linear-gradient(90deg, #f87171 0%, #449AEF 100%)",
                    boxShadow: "0 4px 16px rgba(239,68,68,0.25)",
                    transform: "scale(1.02)",
                  },
                  cursor: "pointer",
                  p: 1,
                  borderRadius: '50%',
                }}
                onClick={() => {
                  setOpenAddCouponModal(true);
                  setEditingId(null);
                }}
              >
                <AddIcon />
              </IconButton>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: "#449AEF", fontSize: "1.2rem", letterSpacing: 0.2 }}
              >
                Add Coupon
              </Typography>
            </Box>
          )}
        />
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1.5, mb: { xs: 2, sm: 0 } }}>
              <Button
                variant="contained"
                startIcon={<FileDownload />}
                onClick={handleExportCSV}
                sx={{
                  background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  borderRadius: '6px',
                  px: 2,
                  py: 0.8,
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                    boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                    transform: 'scale(1.02)',
                  },
                  cursor: 'pointer',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Export CSV
              </Button>
              <Button
                variant="contained"
                startIcon={<PictureAsPdf />}
                onClick={handleExportPDF}
                sx={{
                  background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  borderRadius: '6px',
                  px: 2,
                  py: 0.8,
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                    boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                    transform: 'scale(1.02)',
                  },
                  cursor: 'pointer',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Export PDF
              </Button>
              <Button
                variant="contained"
                startIcon={<Print />}
                onClick={handlePrint}
                sx={{
                  background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  borderRadius: '6px',
                  px: 2,
                  py: 0.8,
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                    boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                    transform: 'scale(1.02)',
                  },
                  cursor: 'pointer',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Print
              </Button>
            </Box>
            <Box>
              <TextField
                label="Search Coupons"
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ width: { xs: '100%', sm: '300px' } }}
              />
            </Box>
          </Box>
          <Box sx={{ height: "100%", width: "100%" }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              pagination
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
              }}
              pageSizeOptions={[10, 25, 50, 75, 100]}
                checkboxSelection
              onRowSelectionModelChange={(ids) => setSelectedRowIds(ids)}
              rowSelectionModel={selectedRowIds}
              disableSelectionOnClick
              density="compact"
              experimentalFeatures={{ virtualization: true }}
              loading={coupons.loading}
              autoHeight={true}
              sx={{
                borderRadius: 2,
                fontSize: "0.8rem",
                "& .MuiDataGrid-cell": {
                  fontSize: "0.8rem",
                  border: "1px solid rgb(204, 201, 201)",
                  minHeight: "36px !important",
                  height: "36px !important",
                  lineHeight: "1.2",
                  paddingTop: "4px",
                  paddingBottom: "4px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "#449AEF",
                  color: "#fff",
                  fontWeight: "bold",
                  fontSize: "0.8rem",
                  padding: "8px",
                  border: "1px solid rgb(204, 201, 201)",
                  minHeight: "36px !important",
                  height: "36px !important",
                  lineHeight: "1.2",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: "0.8rem",
                  fontWeight: 600,
                },
                "& .MuiDataGrid-row": {
                  maxHeight: "36px !important",
                  minHeight: "36px !important",
                },
                "& .MuiDataGrid-row:nth-of-type(odd)": {
                  backgroundColor: "#f5f5f5",
                },
                "& .MuiDataGrid-row:nth-of-type(even)": {
                  backgroundColor: "#ffffff",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
              getRowId={(row) => row.id}
            />
          </Box>
        </CardContent>
      </Card>
    </div>
  );
};

export default Coupons;
